/**
 * The "Spellulator" — an in-game replica of a bolus calculator.
 *
 * IMPORTANT: This is GAME MATH for a fictional game world. It exists to teach
 * kids the HABIT of using a bolus calculator instead of guessing or "feeling"
 * a dose. It must never be used for real-world dosing. Real bolus calculators
 * live in pump/CGM apps configured by the kid's own care team.
 *
 * Teaching intent:
 *  - meal dose = carbs / carb ratio
 *  - correction only above a threshold, minus active (onboard) insulin
 *  - safe rounding to the pump's minimum increment
 *  - the calculator's answer is the correct answer — the game rewards TRUSTING
 *    the tool, and never lets the player "tune" the result by gut feel.
 */

export interface BolusSettings {
  /** grams of carbs covered by 1 unit (game-world value) */
  carbRatioGPerUnit: number;
  /** mg/dL dropped per 1 unit (game-world value) */
  correctionFactorMgdlPerUnit: number;
  /** correction target (game-world value) */
  targetGlucoseMgdl: number;
  /** only correct when current glucose is at or above this */
  correctionAboveMgdl: number;
  /** insulin already working from an earlier dose */
  activeInsulinUnits: number;
  /** minimum displayable/incrementable dose */
  minIncrementUnits: number;
  /** hard cap so silly choices stay bounded */
  maxDoseUnits: number;
}

/** Game-world defaults for the MVP quest. */
export const DEFAULT_GAME_BOLUS_SETTINGS: BolusSettings = {
  carbRatioGPerUnit: 12,
  correctionFactorMgdlPerUnit: 45,
  targetGlucoseMgdl: 110,
  correctionAboveMgdl: 150,
  activeInsulinUnits: 0,
  minIncrementUnits: 0.5,
  maxDoseUnits: 25,
};

export function mealDoseUnits(carbsGrams: number, carbRatioGPerUnit: number): number {
  if (carbsGrams <= 0 || carbRatioGPerUnit <= 0) return 0;
  return carbsGrams / carbRatioGPerUnit;
}

export function correctionDoseUnits(
  currentGlucoseMgdl: number,
  s: BolusSettings,
): number {
  if (currentGlucoseMgdl < s.correctionAboveMgdl) return 0;
  const raw =
    (currentGlucoseMgdl - s.targetGlucoseMgdl) / s.correctionFactorMgdlPerUnit -
    s.activeInsulinUnits;
  return Math.max(0, raw);
}

/** Round to the nearest pump increment, defensively cleaning float noise. */
export function roundToIncrement(units: number, increment: number): number {
  if (increment <= 0) return Math.max(0, units);
  const steps = Math.round(units / increment);
  return Math.max(0, Math.round(steps * increment * 1000) / 1000);
}

/** The one true game dose: meal + correction, clamped and safely rounded. */
export function totalBolusUnits(
  carbsGrams: number,
  currentGlucoseMgdl: number,
  s: BolusSettings = DEFAULT_GAME_BOLUS_SETTINGS,
): number {
  const total = mealDoseUnits(carbsGrams, s.carbRatioGPerUnit) + correctionDoseUnits(currentGlucoseMgdl, s);
  const clamped = Math.min(Math.max(0, total), s.maxDoseUnits);
  return roundToIncrement(clamped, s.minIncrementUnits);
}
